import type { LoanOptionsWithPricing } from '../../../serviceLayer/getLoanOptionsPricingAndScores/POST/response'

/**
 * Normalized, partner-facing lender decision. Derived from Motiv's raw lenderDecision:
 * Accept/Conditional -> accepted, Processing -> processing, Decline/NoQuote/TimedOut -> declined.
 */
export enum LENDER_STATUS {
  ACCEPTED = 'accepted',
  PROCESSING = 'processing',
  DECLINED = 'declined',
}

/** A single lender pre-approval returned to a partner. Partner-facing fields only. */
export interface PartnerLenderOption
  extends Pick<
    LoanOptionsWithPricing,
    | 'publicId'
    | 'displayName'
    | 'isEligible'
    | 'interestRate'
    | 'comparisonRate'
    | 'monthly'
    | 'term'
    | 'balloonAmount'
    | 'originationFee'
    | 'brokerageFee'
  > {
  status: LENDER_STATUS
  /**
   * True when the lender offered different terms to those requested (a different loan amount or
   * term). Returned so partners whose site cannot present counter offers can filter them out.
   */
  isCounterOffer?: boolean
  /**
   * Publicly reachable lender logo asset (sourced from the loan option's meta.logo). Omitted when the
   * lender has no logo available, so partners never render a broken image.
   */
  logoUrl?: string
  /** Portal deep-link to this lender's reveal (customer-facing, browser handover). */
  applyLink?: string
  /**
   * Server-to-server link that records this lender as the partner's choice (confirm + advance
   * status) without sending the customer through the browser. Present only on accepted lenders.
   */
  confirmLink?: string
}

/** Response body for POST /v1/quote/submit-and-price. */
export interface SubmitAndPriceResponse {
  /** Number of lenders with a firm accept (Accept/Conditional). */
  lenderAcceptedCount: number
  lenders: PartnerLenderOption[]
}
